import React from "react";
import { FoodDto, PurchaseDto } from "../../api";
import { MealIngredient } from "../../data/mealWithIngredients";
import { MealWithIngredients } from "../../data/mealWithIngredients";
import { emptyNutriValue, NutriValues } from "../../data/nutriValues";
import { cn } from "../../lib/utils";
import { UnitAmount } from "../UnitAmount";
import { FoodUnit } from "../../data/foodUnit";
import { findById, isNullIndex } from "../../utils/planUtils";
import {
  convertByNetWeight,
  formatPurchaseDescription,
} from "../../utils/purchaseUtils";
import { useFoodAsync } from "../../queries/caches";
import {
  IngredientEdit,
  IngredientMeta,
  PlannedEdit,
  PLAN_VALUE,
  productEditFor,
  resolveRowView,
} from "./plannedIntakeUtils";
import { useIngredientMeta, useMealEditNutri } from "./intakeLookups";
import { ProductPickerDrawer, ProductChoice } from "../ProductPickerDrawer";
import { DishCard } from "../dish/DishCard";
import { SmallIconButton } from "../SmallIconButton";
import {
  IngredientRestoreRow,
  IngredientRow,
} from "../dish/IngredientRow";
import {
  DishModification,
  StepIngredientModification,
} from "../../data/dishModification";

export interface ConsumeRow {
  highlighted: boolean;
  key: string;
  name: string;
  status?: string;
  units: FoodUnit[];
  currentUnit: FoodUnit | undefined;
  amount: number;
}

type SiWithStep = StepIngredientModification & {
  stepPos: number;
  stepText?: string | null;
};

function flattenDishSteps(dish: DishModification | undefined): SiWithStep[] {
  return (dish?.steps ?? []).flatMap((step) =>
    step.ingredients.map((si) => ({
      ...si,
      stepPos: step.position ?? 0,
      stepText: step.text || null,
    })),
  );
}

function matchesIngredient(si: SiWithStep, mi: MealIngredient): boolean {
  return (
    (si.foodUnitId != null && si.foodUnitId === mi.foodUnitId) ||
    !!si.alternatives?.some((alt) => alt.foodUnitId === mi.foodUnitId)
  );
}

function findMatchingSi(
  allSis: SiWithStep[],
  mi: MealIngredient,
): SiWithStep | undefined {
  return allSis.find((si) => matchesIngredient(si, mi));
}

function alternativeUnitIds(si: SiWithStep | undefined): number[] {
  if (!si) return [];
  const ids: number[] = [];
  if (si.foodUnitId != null) ids.push(si.foodUnitId);
  si.alternatives?.forEach((alt) => {
    if (alt.foodUnitId != null) ids.push(alt.foodUnitId);
  });
  return [...new Set(ids)];
}

/** Nährwerte der aktuell gewählten Zutat (Menge/Einheit/Produkt aus `view`). */
function rowNutri(
  meta: IngredientMeta,
  food: FoodDto | undefined,
  purchase: PurchaseDto | undefined,
  view: ConsumeRow,
): NutriValues {
  const amount = view.amount ?? 0;
  const nw = view.currentUnit?.netWeight;
  const factor = (nw != null ? nw / 100 : 1) * amount;
  const source = !meta.isFoodBased || purchase ? purchase : food;
  if (!source) return { ...emptyNutriValue };
  return {
    fat: source.fat * factor,
    carbs: source.carbs * factor,
    protein: source.protein * factor,
    kcal: (source.kcal ?? 0) * factor,
  };
}

function PlannedCheckbox(props: {
  checked: boolean;
  onToggle: () => void;
  dataCy?: string;
}) {
  return (
    <button
      type="button"
      data-cy={props.dataCy}
      aria-pressed={props.checked}
      aria-label="Als verzehrt markieren"
      onClick={props.onToggle}
      className={cn(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-control border transition",
        props.checked
          ? "border-teal bg-teal text-white"
          : "border-line bg-surface-card text-transparent [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink-muted",
      )}
    >
      <svg
        aria-hidden
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </button>
  );
}

function PlannedIngredientRow(props: {
  mi: MealIngredient;
  edit: IngredientEdit | undefined;
  alternativeUnitIds: number[];
  stepInfo?: { stepIndex: number; stepText?: string | null };
  isLast: boolean;
  onOpenProductPicker: () => void;
  onApplyEdit: (partial: IngredientEdit) => void;
}) {
  const { meta, food, purchase } = useIngredientMeta(props.mi, props.edit);
  const view = resolveRowView(meta, props.edit, meta.purchases);

  return (
    <IngredientRow
      isLast={props.isLast}
      title={view.name}
      alternativeUnitIds={props.alternativeUnitIds}
      activeFoodId={food?.id}
      onSelectAlternative={(_food, unitId) =>
        props.onApplyEdit({ unitId, purchaseId: null })
      }
      stepInfo={
        props.stepInfo
          ? { index: props.stepInfo.stepIndex, text: props.stepInfo.stepText }
          : undefined
      }
      amount={
        <UnitAmount
          foodUnit={view.currentUnit}
          units={view.units}
          amount={view.amount}
          onUnitChange={(u) =>
            props.onApplyEdit({
              amount: convertByNetWeight(
                view.amount,
                view.currentUnit?.netWeight,
                u?.netWeight,
              ),
              unitId: u?.id,
            })
          }
          onAmountChange={(a) => props.onApplyEdit({ amount: a })}
        />
      }
      onDelete={() => props.onApplyEdit({ deleted: true })}
      deleteLabel="Zutat entfernen"
      nutri={rowNutri(meta, food, purchase, view)}
      product={{
        dataCy: "verzehr-product-picker-button",
        // Gold nur solange noch kein Produkt (oder Planwert) bestätigt wurde.
        highlighted: view.highlighted && props.edit?.purchaseId === undefined,
        onClick: props.onOpenProductPicker,
      }}
    />
  );
}

function PlannedIngredientInactiveRow(props: {
  mi: MealIngredient;
  edit: IngredientEdit | undefined;
  onRestore: () => void;
}) {
  const { meta } = useIngredientMeta(props.mi, props.edit);
  return <IngredientRestoreRow name={meta.name} onRestore={props.onRestore} />;
}

export function PlannedConsumeCard(props: {
  meal: MealWithIngredients;
  personName?: string;
  edit: PlannedEdit;
  dish?: DishModification;
  onToggleChecked: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenProductPicker: (ingredientKey: string) => void;
  onApplyEdit: (ingredientKey: string, partial: IngredientEdit) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { meal, dish } = props;
  const nutri = useMealEditNutri(meal, props.edit);
  const description =
    meal.dishDescription || meal.foodDescription || meal.purchaseDescription || "";
  const hasPencil =
    !isNullIndex(meal.dishId) ||
    !isNullIndex(meal.foodId) ||
    !isNullIndex(meal.purchaseId);

  const allSis = flattenDishSteps(dish);
  const foodIdCounts = new Map<number, number>();
  meal.mealIngredients.forEach((mi) => {
    if (!isNullIndex(mi.foodUnitFoodId)) {
      foodIdCounts.set(
        mi.foodUnitFoodId!,
        (foodIdCounts.get(mi.foodUnitFoodId!) ?? 0) + 1,
      );
    }
  });

  const byStepIndex = (a: MealIngredient, b: MealIngredient) =>
    (a.stepIndex ?? Number.MAX_SAFE_INTEGER) -
    (b.stepIndex ?? Number.MAX_SAFE_INTEGER);
  const activeIngredients = meal.mealIngredients
    .filter((mi) => !props.edit.ingredients[String(mi.id)]?.deleted)
    .sort(byStepIndex);
  const deletedIngredients = meal.mealIngredients
    .filter((mi) => props.edit.ingredients[String(mi.id)]?.deleted)
    .sort(byStepIndex);

  return (
    <DishCard
      dataCy="verzehr-planned-card"
      title={description}
      personName={props.personName}
      kcal={nutri.kcal}
      nutri={nutri}
      borderClassName={props.edit.checked ? "border-teal" : "border-line"}
      leading={
        <PlannedCheckbox
          dataCy="verzehr-planned-toggle"
          checked={props.edit.checked}
          onToggle={props.onToggleChecked}
        />
      }
      expanded={props.expanded}
      onToggleExpand={props.onToggleExpand}
      titleDataCy="verzehr-planned-card-title"
      actions={
        <>
          {hasPencil && (
            <SmallIconButton
              label="Gericht/Lebensmittel bearbeiten"
              data-cy="verzehr-pencil-button"
              icon="pencil"
              onClick={props.onEdit}
            />
          )}
          <SmallIconButton
            label="Eintrag löschen"
            data-cy="verzehr-delete-button"
            icon="delete"
            onClick={props.onDelete}
          />
        </>
      }
    >
      {activeIngredients.map((mi, index) => {
        const key = String(mi.id);
        const edit = props.edit.ingredients[key];
        const si = findMatchingSi(allSis, mi);
        const isDuplicate =
          !isNullIndex(mi.foodUnitFoodId) &&
          (foodIdCounts.get(mi.foodUnitFoodId!) ?? 0) > 1 &&
          mi.stepIndex != null;
        const stepInfo = isDuplicate
          ? { stepIndex: mi.stepIndex!, stepText: mi.stepText }
          : undefined;
        return (
          <PlannedIngredientRow
            key={key}
            mi={mi}
            edit={edit}
            alternativeUnitIds={alternativeUnitIds(si)}
            stepInfo={stepInfo}
            isLast={index === activeIngredients.length - 1}
            onOpenProductPicker={() => props.onOpenProductPicker(key)}
            onApplyEdit={(partial) => props.onApplyEdit(key, partial)}
          />
        );
      })}
      {deletedIngredients.map((mi) => {
        const key = String(mi.id);
        return (
          <PlannedIngredientInactiveRow
            key={key}
            mi={mi}
            edit={props.edit.ingredients[key]}
            onRestore={() =>
              props.onApplyEdit(key, {
                deleted: false,
                amount: undefined,
                unitId: undefined,
                purchaseId: undefined,
              })
            }
          />
        );
      })}
    </DishCard>
  );
}

export function PlannedProductPicker(props: {
  mi: MealIngredient;
  edit: IngredientEdit | undefined;
  onApply: (partial: IngredientEdit) => void;
  onClose: () => void;
}) {
  const { meta, food, purchase } = useIngredientMeta(props.mi, props.edit);
  const purchaseFood = useFoodAsync(
    meta.isFoodBased ? undefined : purchase?.foodId,
  );
  const createFood = meta.isFoodBased ? food : purchaseFood;

  const alternatives = meta.purchases;
  const defaultPurchase =
    meta.defaultPurchaseId != null
      ? findById(alternatives, meta.defaultPurchaseId)
      : undefined;

  // Aktuell gewählte FoodUnit (Alternative) – auch wenn bereits ein Purchase
  // erfasst ist (dann aus dessen PurchaseUnit herleiten).
  const activeFoodUnitId = (() => {
    if (!meta.isFoodBased) return undefined;
    if (
      props.edit?.unitId != null &&
      meta.planUnits.some((u) => u.id === props.edit!.unitId)
    ) {
      return props.edit.unitId;
    }
    const selectedPU = purchase?.purchaseUnits?.find(
      (pu) => pu.id === props.edit?.unitId,
    );
    return (
      selectedPU?.foodUnitId ??
      meta.planUnits.find((u) => u.isDefault)?.id ??
      meta.planUnits[0]?.id
    );
  })();
  const selectedId =
    props.edit?.purchaseId == null ||
    props.edit?.purchaseId === meta.planPurchaseId
      ? null
      : props.edit?.purchaseId;
  const planPurchase =
    meta.planPurchaseId != null
      ? findById(alternatives, meta.planPurchaseId)
      : undefined;
  const planLabel = meta.isFoodBased
    ? "Planwert"
    : formatPurchaseDescription(planPurchase) || meta.name;
  // Baseline für die +/- kcal-Anzeige: bei food-based der Standard-Purchase,
  // bei purchase-based das geplante Kaufprodukt.
  const baselineKcal = meta.isFoodBased
    ? defaultPurchase?.kcal
    : planPurchase?.kcal;
  const choices: ProductChoice[] = [
    {
      id: null,
      label: planLabel,
      kcal: baselineKcal,
    },
    ...alternatives
      .filter((p) => p.id !== meta.planPurchaseId)
      .filter(
        (p) =>
          meta.defaultPurchaseId == null || p.id !== meta.defaultPurchaseId,
      )
      .map((p) => ({
        id: p.id,
        label: formatPurchaseDescription(p),
        kcal: p.kcal,
      })),
  ];

  return (
    <ProductPickerDrawer
      open
      onClose={props.onClose}
      title={meta.name}
      choices={choices}
      selectedId={selectedId}
      baselineKcal={baselineKcal}
      createFood={createFood}
      onSelect={(id) => {
        props.onApply(
          productEditFor(
            meta,
            props.edit,
            id === null ? PLAN_VALUE : String(id),
            alternatives,
            activeFoodUnitId,
          ),
        );
        props.onClose();
      }}
    />
  );
}
